<script setup lang="ts">
import { CrossSell, Trainer, TranslatedProduct } from "../../types/PersologOnlineTypes";
import { PropType, ref, watch, computed } from "vue";
import BuyButton from "../common/BuyButton.vue";
import { ProfileAdministrationAPI } from "../../api";
import FavoriteButton from "../profiles/FavoriteButton.vue";
import { fetchData, filterNotPurchased } from "../../utils/ProductViewUtils";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const emit = defineEmits(["update", "empty-carousel"]);

const imageBasePath = "./images/profile-cover/";

const props = defineProps({
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    profiles: {
        type: Array as PropType<TranslatedProduct[]>,
        required: true,
    },
});

/**
 * Cross-sell relation data
 */
const crossSellData = ref<CrossSell>();

/**
 * Carousel data for cross-sell products. This data is the input for the carousel component
 */
const carouselData = ref<{ id: string; code: string; name: string; image: string; model: string }[]>([]);

/**
 * Products that have not been purchased yet by the trainer
 */
const notPurchased = ref<TranslatedProduct[]>([]);

/**
 * Responsive layout for carousel items based on screen width
 */
const responsiveOptions = ref([
    {
        breakpoint: "1400px",
        numVisible: 2,
        numScroll: 1,
    },
    {
        breakpoint: "1199px",
        numVisible: 3,
        numScroll: 1,
    },
    {
        breakpoint: "767px",
        numVisible: 2,
        numScroll: 1,
    },
    {
        breakpoint: "575px",
        numVisible: 1,
        numScroll: 1,
    },
]);

/**
 * Only enable circular mode when we have enough items, to prevent erroneous blank spaces in the carousel
 */
const shouldUseCircular = computed(() => {
    return carouselData.value.length > 3;
});

/**
 * Handles event when a product is purchased from the carousel
 * @param product The product code of the purchased product
 */
function handlePurchase(product: string) {
    emit("update", true, product);
}

/**
 * Handles event when a product is favorited from the carousel
 * @param product The product code of the favorited product
 */
function handleFavorite(product: string) {
    emit("update", true, product);
}

/**
 * Fetches products that have not been purchased by the trainer
 */
async function fetchNotPurchased() {
    const { purchased, all } = await fetchData();
    notPurchased.value = filterNotPurchased(all, purchased);
}

/**
 * Fetches cross-sell relations for the given profiles and trainer language
 */
async function fetchCrossSellData() {
    crossSellData.value = await ProfileAdministrationAPI.getCrossSellProducts(
        props.profiles.map((profile) => profile.productNo),
        props.trainer.language,
    );
}

/**
 * Fetches cross-sell products for the given profiles and trainer language
 * and sets the carousel data with the fetched products and their translations
 */
function getCarouselData() {
    if (props.profiles && props.trainer && props.profiles.length > 0) {
        carouselData.value = [];

        //seenIds is used to prevent duplicate products in the carousel
        const seenIds = new Set();
        crossSellData.value?.crossSellProducts.forEach((product) => {
            if (seenIds.has(product.product)) {
                return;
            }

            seenIds.add(product.product);
            const translatedProduct = notPurchased.value.find((profile) => profile.productNo === product.product);
            carouselData.value.push({
                id: product.product,
                code: product.product,
                name: translatedProduct?.productName || "",
                image: product.picture,
                model: translatedProduct?.modelNo || "",
            });
        });
        carouselData.value.length == 0 ? emit("empty-carousel", true) : emit("empty-carousel", false);
    }
}

/**
 * Fetches cross-sell products on mounted and whenever the profiles change
 */
watch(
    () => props.profiles,
    async () => {
        await fetchCrossSellData();
        await fetchNotPurchased();
        await getCarouselData();
    },
    { immediate: true },
);
</script>

<template>
    <Carousel
        :value="carouselData"
        :num-visible="3"
        :num-scroll="1"
        :circular="shouldUseCircular"
        :responsive-options="responsiveOptions"
        v-if="carouselData.length > 0"
    >
        <template #item="slotProps">
            <div class="m-2 p-4 carousel-card">
                <div class="mb-4 card carousel-item flex justify-center w-full relative mx-auto">
                    <Image :alt="t('accessibility.images.productImage', { productName: slotProps.data.name })" preview class="carousel-image-wrapper">
                        <template #image>
                            <img
                                :src="imageBasePath + slotProps.data.image"
                                :alt="t('accessibility.images.productImage', { productName: slotProps.data.name })"
                                class="carousel-image"
                            />
                        </template>
                        <template #original="slotPropsPreview">
                            <img
                                :src="imageBasePath + slotProps.data.image"
                                :alt="t('accessibility.images.productPreview', { productName: slotProps.data.name })"
                                :style="slotPropsPreview.style"
                                @click="slotPropsPreview.previewCallback"
                            />
                        </template>
                    </Image>
                    <FavoriteButton
                        :product="slotProps.data.code"
                        :product-name="slotProps.data.name"
                        :trainer="props.trainer"
                        @update="handleFavorite(slotProps.data.code)"
                        size="large"
                        class="absolute top-0 right-0 mt-2 mr-2 rounded-full shadow-lg"
                    />
                </div>

                <div class="w-full items-center">
                    <div class="mb-4 font-medium font-bold text-center">{{ slotProps.data.name }}</div>
                    <BuyButton
                        :product="slotProps.data.code"
                        :product-name="slotProps.data.name"
                        :trainer="props.trainer"
                        :with-label="true"
                        @update="handlePurchase(slotProps.data.code)"
                        with-classes="carousel-buy-button w-full"
                    />
                </div>
            </div>
        </template>
    </Carousel>
</template>

<style>
.carousel-card {
    height: 300px;
    display: flex;
    flex-direction: column;
}

.carousel-item {
    flex: 4;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 0;
}

.carousel-image-wrapper,
.carousel-item .p-image {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
}

.carousel-image {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: center;
}

.carousel-card > div:last-child {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.carousel-buy-button {
    max-width: 300px;
}
</style>
